import { useCallback, useEffect, useRef, useState } from 'react'
import type { Thread } from '@dev-streetbeat/chat/types'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

import { AgentApp } from '../../agent/App'
import type { AgentConfig } from '../../agent/types'
import type { ClientRef } from '../../agent/composer-context/types'
import { fetchBankerAum, fetchHomeNba } from '../api/explorer'
import { BankerAumCard } from '../components/credem/BankerAumCard'
import { HomeNbaCard, HomeNbaCardSkeleton } from '../components/credem/HomeNbaCard'
import { HomeNbaEmpty } from '../components/credem/HomeNbaEmpty'
import type { BankerAumPayload, HomeNbaAction, HomeNbaPayload } from '../contracts'
import type { AuthUser } from '../types'
import { FtueRoot } from '../ftue'
import { fetchScheduleRunChatPrompt } from '../api/schedules'

// Duration of the card dismiss exit animation in ms. Must match the CSS
// keyframe in `HomeNbaCard.module.scss` so the slot swap commits at the
// moment the old card finishes fading out.
const NBA_CARD_EXIT_MS = 220

// Fixed slot count for the homepage grid. The frontend renders this many
// cards regardless of how many candidates the backend returned.
const NBA_VISIBLE_SLOTS = 3

type SchedulePromptState = {
  schedulePrompt?: string
  autoSendSchedulePrompt?: boolean
  /**
   * Cross-route hand-off carried from the Da fare row actions. Pins the
   * referenced client into the composer chip on chat mount; when paired with
   * `schedulePrompt` + `autoSendSchedulePrompt`, the chat awaits the chip
   * before auto-sending so the outgoing turn is correctly scoped.
   */
  scheduleClientContext?: ClientRef
}

// Sidebar "Nuova chat" hand-off. The sidebar pushes to `/` with this flag
// to swap the dashboard layout for a focused composer (centered heading +
// chat input only). Kept separate from `SchedulePromptState` because the
// two hand-offs originate from independent entry points.
type NewChatNavState = {
  newChat?: boolean
}

export function HomeRoute({
  authUser,
  config,
  onThreadChange,
  threadId,
}: {
  authUser: AuthUser | null
  config?: Partial<AgentConfig>
  onThreadChange?: (threadId: string | null, thread?: Thread | null) => void
  threadId?: string | null
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const firstName = authUser?.name?.split(/\s+/)[0]
  const [chatStarted, setChatStarted] = useState(false)
  const shouldShowChatOnly = chatStarted || Boolean(threadId)
  const handleConversationStartedChange = useCallback((started: boolean) => {
    setChatStarted(started)
  }, [])

  // Focused-composer state for the sidebar "Nuova chat" entry point. The
  // sidebar pushes to `/` with `state.newChat = true`; the Dashboard nav
  // item pushes with no state. We derive the mode from the navigation
  // action so:
  //  - `PUSH` with `state.newChat` → focused (sidebar Nuova chat).
  //  - `PUSH` without it → dashboard (Dashboard nav, or any other entry).
  //  - `POP` → dashboard (refresh, browser back/forward).
  // This pattern means the dashboard is always the homepage and the
  // focused state is purely a session intent.
  const [newChatMode, setNewChatMode] = useState<boolean>(() => {
    if (navigationType === 'POP') return false
    return Boolean((location.state as NewChatNavState | null)?.newChat)
  })

  useEffect(() => {
    if (navigationType === 'POP') {
      setNewChatMode(false)
      return
    }
    setNewChatMode(Boolean((location.state as NewChatNavState | null)?.newChat))
  }, [location.key, location.state, navigationType])

  // The chat's imperative send fn, registered by AgentApp once the inner
  // chat-runtime provider is mounted. Captured into a ref so the home
  // action cards can push prompts into the chat without prop-drilling
  // state through the agent boundary.
  const sendRef = useRef<((text: string) => void) | null>(null)
  const handleRegisterSend = useCallback((fn: (text: string) => void) => {
    sendRef.current = fn
  }, [])
  const handleCardClick = useCallback((prompt: string) => {
    if (!prompt) return
    sendRef.current?.(prompt)
  }, [])

  const schedulePromptState = location.state as SchedulePromptState | null
  const [scheduledInitialPrompt] = useState(() =>
    schedulePromptState?.autoSendSchedulePrompt && schedulePromptState.schedulePrompt?.trim()
      ? schedulePromptState.schedulePrompt.trim()
      : undefined,
  )
  const [scheduledInitialClientContext] = useState<ClientRef | undefined>(
    () => schedulePromptState?.scheduleClientContext ?? undefined,
  )
  const [urlInitialPrompt, setUrlInitialPrompt] = useState<string | undefined>()
  const [aum, setAum] = useState<BankerAumPayload | null>(null)
  const [aumLoading, setAumLoading] = useState(true)
  // Home-NBA grid. Empty payload is the honest signal (no priorities surface
  // today); the dashboard widget fails soft instead of toasting an error.
  const [homeNba, setHomeNba] = useState<HomeNbaPayload | null>(null)
  const [homeNbaLoading, setHomeNbaLoading] = useState(true)

  // Slot-locked grid state. Each slot is an independent position whose
  // content can be replaced without shifting the other slots. This is
  // what lets the dismiss animation read as "this card is being
  // replaced" rather than "the row reshuffles".
  //
  // - `slots`: fixed-length array of the card currently shown at each
  //   position (or null when no replacement is available).
  // - `dismissedCodes`: every bucket the banker has dismissed in this
  //   session. Used to pick a replacement that hasn't been seen yet.
  // - `exitingCode`: the bucket currently animating out. The render
  //   keeps the card in the DOM (so its exit animation can play) and
  //   the slot only updates after the animation commits.
  // - `exitLockRef`: prevents reentrancy if the banker hammers the X
  //   button while a dismiss is mid-animation.
  //
  // All of this resets when a new payload arrives (refresh / new
  // session), which matches the "NBAs are recalculated" expectation.
  const [slots, setSlots] = useState<(HomeNbaAction | null)[]>(() =>
    new Array(NBA_VISIBLE_SLOTS).fill(null),
  )
  const [dismissedCodes, setDismissedCodes] = useState<Set<string>>(() => new Set())
  const [exitingCode, setExitingCode] = useState<string | null>(null)
  const exitLockRef = useRef(false)

  useEffect(() => {
    if (!homeNba) {
      setSlots(new Array(NBA_VISIBLE_SLOTS).fill(null))
      setDismissedCodes(new Set())
      setExitingCode(null)
      exitLockRef.current = false
      return
    }
    setSlots(Array.from({ length: NBA_VISIBLE_SLOTS }, (_, i) => homeNba.actions[i] ?? null))
    setDismissedCodes(new Set())
    setExitingCode(null)
    exitLockRef.current = false
  }, [homeNba])

  const handleDismiss = useCallback(
    (bucketCode: string) => {
      if (exitLockRef.current) return
      const targetSlot = slots.findIndex((s) => s?.bucket_code === bucketCode)
      if (targetSlot === -1) return

      exitLockRef.current = true
      setExitingCode(bucketCode)

      window.setTimeout(() => {
        const nextDismissed = new Set(dismissedCodes)
        nextDismissed.add(bucketCode)

        const pool = [...(homeNba?.actions ?? []), ...(homeNba?.reserve ?? [])]
        const stillVisible = new Set(
          slots.filter((s, i) => s !== null && i !== targetSlot).map((s) => s!.bucket_code),
        )
        const replacement =
          pool.find((a) => !nextDismissed.has(a.bucket_code) && !stillVisible.has(a.bucket_code)) ??
          null

        const nextSlots = [...slots]
        nextSlots[targetSlot] = replacement

        setDismissedCodes(nextDismissed)
        setSlots(nextSlots)
        setExitingCode(null)
        exitLockRef.current = false
      }, NBA_CARD_EXIT_MS)
    },
    [slots, dismissedCodes, homeNba],
  )

  const hasAnyVisibleCard = slots.some((s) => s !== null)

  // Full local reset for the "Riesamina priorità" affordance in the
  // dismissed-all empty state. NBAs recalculate on the daily cadence —
  // this only undoes the banker's in-session dismissals, restoring the
  // grid to the same shape it had at first paint. Reserve cards become
  // candidates again the next time the banker dismisses something.
  const handleRestore = useCallback(() => {
    if (!homeNba) return
    setSlots(Array.from({ length: NBA_VISIBLE_SLOTS }, (_, i) => homeNba.actions[i] ?? null))
    setDismissedCodes(new Set())
    setExitingCode(null)
    exitLockRef.current = false
  }, [homeNba])

  // Distinguishes the two empty contexts that share this row. Without
  // any dismissals on record, the firing pool was simply empty for this
  // banker today; with dismissals on record, the banker has cleared the
  // deck and the restore affordance is offered.
  const emptyVariant: 'all-reviewed' | 'no-priorities' =
    dismissedCodes.size > 0 ? 'all-reviewed' : 'no-priorities'

  useEffect(() => {
    if (!scheduledInitialPrompt && !scheduledInitialClientContext) return
    navigate('.', { replace: true, state: null })
  }, [navigate, scheduledInitialPrompt, scheduledInitialClientContext])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const runId = params.get('scheduled_run_id')?.trim()
    const autoSend = params.get('chat_autosend') === '1'
    if (!runId || !autoSend) return

    const controller = new AbortController()
    fetchScheduleRunChatPrompt(runId, controller.signal)
      .then((payload) => {
        const prompt = payload.prompt.trim()
        if (prompt) setUrlInitialPrompt(prompt)
      })
      .catch(() => {
        // If the run cannot be resolved, leave the chat idle after cleaning
        // the one-shot URL parameters.
      })
      .finally(() => {
        if (controller.signal.aborted) return
        params.delete('scheduled_run_id')
        params.delete('chat_autosend')
        const query = params.toString()
        navigate(query ? `?${query}` : '.', { replace: true })
      })
    return () => controller.abort()
  }, [location.search, navigate])

  useEffect(() => {
    const controller = new AbortController()
    setAumLoading(true)
    fetchBankerAum(controller.signal)
      .then((payload) => {
        if (!controller.signal.aborted) setAum(payload)
      })
      .catch(() => {
        // Dashboard widget: failing softly is preferable to surfacing an
        // error toast. The placeholder treatment is shown instead.
      })
      .finally(() => {
        if (!controller.signal.aborted) setAumLoading(false)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setHomeNbaLoading(true)
    fetchHomeNba(controller.signal)
      .then((payload) => {
        if (!controller.signal.aborted) setHomeNba(payload)
      })
      .catch(() => {
        // Same failure posture as the AUM card — keep the home stable
        // and show the empty-state line instead of an error.
        if (!controller.signal.aborted) setHomeNba({ actions: [], reserve: [], generated_at: '' })
      })
      .finally(() => {
        if (!controller.signal.aborted) setHomeNbaLoading(false)
      })
    return () => controller.abort()
  }, [])

  return (
    <div
      className="home-route"
      data-chat-started={shouldShowChatOnly ? 'true' : 'false'}
      data-new-chat={newChatMode ? 'true' : 'false'}
    >
      {!shouldShowChatOnly ? (
        <section className="home-route__intro">
          <div className="home-route__intro-row">
            <div className="home-route__intro-heading">
              {newChatMode ? (
                <h2 className="text-display-l text-foreground">Cosa vorresti fare oggi?</h2>
              ) : (
                <>
                  {firstName ? (
                    <p className="text-body-emphasis text-foreground">Benvenuto, {firstName}</p>
                  ) : null}
                  <h2 className="text-display-l text-foreground">Cosa vorresti fare oggi?</h2>
                </>
              )}
            </div>
            {newChatMode ? null : <BankerAumCard aum={aum} loading={aumLoading} />}
          </div>
        </section>
      ) : null}

      {!shouldShowChatOnly && !newChatMode ? (
        <section
          aria-label="Azioni suggerite"
          className="home-route__actions grid gap-4 md:grid-cols-3 [&:hover>*:not(:hover)]:opacity-50"
        >
          {homeNbaLoading ? (
            <>
              <HomeNbaCardSkeleton />
              <HomeNbaCardSkeleton />
              <HomeNbaCardSkeleton />
            </>
          ) : hasAnyVisibleCard ? (
            slots.map((action, index) =>
              action ? (
                // Keying by bucket_code lets React unmount the old card
                // and mount the replacement when the slot's bucket
                // changes — each transition gets its own enter animation.
                <HomeNbaCard
                  key={action.bucket_code}
                  action={action}
                  position={index}
                  isExiting={action.bucket_code === exitingCode}
                  onClick={handleCardClick}
                  onDismiss={handleDismiss}
                />
              ) : (
                <div key={`empty-${index}`} aria-hidden />
              ),
            )
          ) : (
            <HomeNbaEmpty
              variant={emptyVariant}
              onRestore={emptyVariant === 'all-reviewed' ? handleRestore : undefined}
            />
          )}
        </section>
      ) : null}

      <section aria-label="Chat AI" className="home-route__chat-stage">
        <AgentApp
          config={{
            ...config,
            showSuggestedPrompts: false,
            showWelcomeHeading: false,
            showNewChatButton: false,
            initialPrompt: urlInitialPrompt ?? scheduledInitialPrompt ?? config?.initialPrompt,
            autoSendInitialPrompt: Boolean(urlInitialPrompt ?? scheduledInitialPrompt),
            initialSessionId: threadId ?? undefined,
          }}
          initialClientContext={scheduledInitialClientContext}
          onConversationStartedChange={handleConversationStartedChange}
          onRegisterSend={handleRegisterSend}
          selectedThreadId={threadId}
          onThreadChange={onThreadChange}
          enableFaviconStatus
        />
      </section>

      {newChatMode ? null : (
        /*
          Design exploration — NOT a production FTUE. See the header
          comment in `frontend/src/app/ftue/index.tsx` for status.
        */
        <FtueRoot />
      )}
    </div>
  )
}
